import { queryOptions, useQueries } from '@tanstack/react-query';

import type { Features } from '@/api/core-api/types.gen';
import type { User } from '@/utils/session/fetch-user';

import { featureFlagsControllerFindAll } from '@/api/core-api/sdk.gen';
import { queryKeys } from '@/hooks/api/query-keys';
import { organizationQueryOptions } from '@/hooks/api/use-organization';
import { getClient } from '@/utils/get-api-client';

const EMPTY_SESSION_APPLICABLE_FEATURE_FLAGS: readonly string[] = [];

export const activeFeatureFlagNamesQueryOptions = (
  organizationId: Maybe<string>,
  authUserId: Maybe<string>,
) =>
  queryOptions({
    queryKey: queryKeys.organizations.activeFeatureFlags(organizationId),
    queryFn: () =>
      featureFlagsControllerFindAll({
        client: getClient(authUserId ?? undefined),
        query: { organizationId: organizationId ?? '' },
        throwOnError: true,
      }).then((res) =>
        res.data.filter((name): name is string => typeof name === 'string'),
      ),
    enabled: !!organizationId && !!authUserId,
    staleTime: 60_000,
  });

export function buildSessionApplicableFeatureFlags(
  activeFeatureFlagNames: readonly string[],
  organizationFeatures: Readonly<Partial<Features>>,
): string[] {
  const enabledOrganizationFeatureNames = Object.entries(
    organizationFeatures,
  ).flatMap(([name, enabled]) => (enabled === true ? [name] : []));

  return [
    ...new Set([...activeFeatureFlagNames, ...enabledOrganizationFeatureNames]),
  ].toSorted();
}

export function useSessionApplicableFeatureFlags(
  user: Maybe<User>,
  organizationId: Maybe<string>,
) {
  const researcherId = user?.roles.includes('researcher') ? user.id : undefined;
  const shouldLoadResearcherOrganization = !!organizationId && !!researcherId;
  return useQueries({
    queries: [
      activeFeatureFlagNamesQueryOptions(organizationId, researcherId),
      {
        ...organizationQueryOptions(organizationId ?? '', researcherId),
        enabled: shouldLoadResearcherOrganization,
      },
    ],
    combine: ([activeFeatureFlagsResult, organizationResult]) => {
      const isSettled =
        activeFeatureFlagsResult.status !== 'pending' &&
        organizationResult.status !== 'pending';

      return {
        isSettled,
        data: isSettled
          ? buildSessionApplicableFeatureFlags(
              activeFeatureFlagsResult.data ?? [],
              organizationResult.data?.features ?? {},
            )
          : EMPTY_SESSION_APPLICABLE_FEATURE_FLAGS,
      };
    },
  });
}
